import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Clock, Eye, MessageCircle, Heart, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import SeoHead from '../../components/seo/SeoHead';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const readingTime = (html) => {
  const text = (html || '').replace(/<[^>]*>/g, ' ');
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

/**
 * BlogListPage — magazine style (featured article up top, grid below).
 * Serves as an index page optimized for SEO (title, description, canonical).
 */
const BlogListPage = ({ onBack, onNavigate }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/articles/public?limit=50`);
        if (mounted) setArticles(res.data?.articles || []);
      } catch (e) {
        if (mounted) setArticles([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = q.trim()
    ? articles.filter((a) => (a.title + ' ' + (a.excerpt || '')).toLowerCase().includes(q.toLowerCase()))
    : articles;

  const featured = filtered[0];
  const rest = filtered.slice(1);

  const openArticle = (article) => {
    onNavigate('blog-article', { slug: article.slug, fallbackId: article.id });
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Blog — Conseils santé, beauté & pharmacie à Cocody Angré"
        description="Le blog de la Pharmacie Saint Ange Des Amis : conseils santé, beauté, bébé, maladies chroniques, parapharmacie. Articles rédigés par notre équipe à Angré 7ème Tranche, Cocody."
        canonical="/blog"
      />

      {/* Magazine header */}
      <header className="border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="-ml-3" data-testid="blog-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Accueil
          </Button>
          <div className="hidden sm:block h-6 w-px bg-neutral-200" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C45C26] font-semibold">
              Le magazine santé
            </p>
            <h1 className="text-xl sm:text-2xl font-serif font-bold text-neutral-900">
              Blog Pharmacie Saint Ange Des Amis
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-neutral-100 rounded-full px-3 py-2">
            <Search className="w-4 h-4 text-neutral-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un article…"
              className="bg-transparent outline-none text-sm w-52"
              data-testid="blog-search"
            />
          </div>
        </div>
      </header>

      {/* Mobile search */}
      <div className="md:hidden max-w-6xl mx-auto px-4 pt-4">
        <div className="flex items-center gap-2 bg-neutral-100 rounded-full px-3 py-2">
          <Search className="w-4 h-4 text-neutral-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un article…"
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20 text-neutral-500">Chargement des articles…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-neutral-500 text-lg mb-2">Aucun article publié pour le moment.</p>
            <p className="text-neutral-400 text-sm">Revenez bientôt — nous publions régulièrement.</p>
          </div>
        ) : (
          <>
            {/* Featured article */}
            {featured && (
              <article
                className="grid md:grid-cols-2 gap-6 lg:gap-10 mb-12 cursor-pointer group"
                onClick={() => openArticle(featured)}
                data-testid="blog-featured-article"
              >
                <div className="aspect-[16/10] bg-neutral-100 rounded-2xl overflow-hidden">
                  {featured.image_url ? (
                    <img
                      src={featured.image_url}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300 text-6xl">📰</div>
                  )}
                </div>
                <div className="flex flex-col justify-center">
                  <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-[#C45C26]/30 self-start mb-3">
                    À la une
                  </Badge>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-neutral-900 mb-3 group-hover:text-[#C45C26] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-neutral-600 leading-relaxed mb-4 line-clamp-3">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-neutral-500">
                    <span>{formatDate(featured.published_at || featured.created_at)}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {readingTime(featured.content)} min</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {featured.views || 0}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {featured.comment_count || 0}</span>
                  </div>
                </div>
              </article>
            )}

            {/* Grid of other articles */}
            {rest.length > 0 && (
              <>
                <div className="h-px bg-neutral-200 mb-10" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {rest.map((a) => (
                    <article
                      key={a.id}
                      className="cursor-pointer group"
                      onClick={() => openArticle(a)}
                      data-testid={`blog-card-${a.slug}`}
                    >
                      <div className="aspect-[4/3] bg-neutral-100 rounded-xl overflow-hidden mb-3">
                        {a.image_url ? (
                          <img
                            src={a.image_url}
                            alt={a.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-neutral-300 text-4xl">📰</div>
                        )}
                      </div>
                      <h3 className="text-lg font-serif font-bold text-neutral-900 mb-2 group-hover:text-[#C45C26] transition-colors line-clamp-2">
                        {a.title}
                      </h3>
                      <p className="text-sm text-neutral-600 leading-relaxed line-clamp-2 mb-3">
                        {a.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span>{formatDate(a.published_at || a.created_at)}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {readingTime(a.content)} min</span>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default BlogListPage;
